import { Board, Column, Panel } from '@gamma/ui/layout';
import { ErrorBoundary } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { useContext, useEffect } from 'react';
import { isSafari } from 'react-device-detect';
import { GetHumioDashboardsQuery } from '../../../../shared/generated/graphql';
import { useReloadHumioUI } from '../../../../shared/hooks';
import { i18n } from '../../../../shared/localization';
import { LogScaleContext } from '../../../../shared/providers';
import { hideHumioInnerNav } from '../../../../shared/utils';
import { HumioLoaderOverlay, HumioUnavailable } from '../../components';

export const HumioDashboards = () => {
  const { dashboards } = i18n.pages.humio;

  const {
    iframeKey,
    logScaleDashboardsLoading,
    logScaleDashboardsError,
    logScaleDashboardsData,
    logScaleDashboardParams,
    setLogScaleDashboardParams,
  } = useContext(LogScaleContext);

  useEffect(() => {
    const dashboardParams = logScaleDashboardParams as
      | {
          dashboardId?: string;
        }
      | undefined;
    if (!dashboardParams?.dashboardId) {
      const localName = localStorage.getItem('dashboard_name');
      const localId = localStorage.getItem('dashboard_id');
      if (localName && localId) {
        setLogScaleDashboardParams?.({
          name: localName,
          dashboardId: localId,
        });
      }
    }
  }, [logScaleDashboardParams, setLogScaleDashboardParams]);

  const dashboardParams = logScaleDashboardParams as
    | {
        name?: string;
        dashboardId?: string;
        investigatorApp?: string;
        corelightDashboard?: string;
      }
    | undefined;
  const { name, dashboardId, investigatorApp, corelightDashboard } =
    dashboardParams || {};

  const dashboardDetails = {
    name,
    dashboardId,
    investigatorApp,
    corelightDashboard,
  };

  const { iframeRef, iframeSrc } = useReloadHumioUI({
    iframeType: 'dashboards',
    dashboardDetails,
  });

  localStorage.removeItem('dashboard_link');

  const dashboardData = logScaleDashboardsData as
    | GetHumioDashboardsQuery
    | undefined;
  if (
    logScaleDashboardsError ||
    (dashboardData &&
      !dashboardData.getHumioDashboards?.searchDomain?.dashboards) ||
    (dashboardData &&
      dashboardData.getHumioDashboards?.searchDomain?.dashboards?.length === 0)
  ) {
    return (
      <HumioUnavailable
        unavailable={dashboards.unavailable}
        techSupport={dashboards.techSupport}
      />
    );
  } else if (logScaleDashboardsLoading || !logScaleDashboardsData) {
    // Loading and data can become out of sync so show loading screen
    return (
      <ErrorBoundary page="Search" styleClass="main">
        <Panel>
          <GraphQLReqStatus
            loading={
              Boolean(logScaleDashboardsLoading) || !logScaleDashboardsData
            }
          />
        </Panel>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary page="Dashboards" styleClass="main">
      <Board
        h="100%"
        flex="1 1 auto"
        flexDirection="column"
        data-testid="humio-dashboards"
        margin={0}
        width="auto"
      >
        <Column
          mt={1}
          pb={0}
          flexGrow={1}
          display="flex"
          flexBasis="auto"
          overflow="hidden"
          flexDirection="column"
        >
          {isSafari && <HumioLoaderOverlay reloadKey={iframeKey} />}
          {iframeSrc && (
            <iframe
              title="ui"
              width="100%"
              id="humio-ui"
              data-testid="humio-ui"
              height="calc(100% + 64px)"
              style={{
                flexGrow: 1,
                display: 'flex',
                paddingBottom: 0,
              }}
              key={iframeKey}
              ref={iframeRef}
              src={iframeSrc}
              onLoad={() => hideHumioInnerNav()}
            />
          )}
        </Column>
      </Board>
    </ErrorBoundary>
  );
};
